// src/frontend/js/modern/core/event-manager.js
import { eventBus, EVENTS } from './event-bus.js';
import { queueManager } from './queue.js';
import { fragmentManager } from './fragment-manager.js';
import { validators } from '../shared/validators.js';
import { showSuccess } from '../shared/ui-helpers.js';
import { ensureNative } from '../shared/ui-helpers.js';
import { extractProductId, getWishlistIdFromElement } from '../shared/dom-helpers.js';

class EventManager {
	constructor() {
		this._handlers = new Map();
		this._registered = false;
		this._qtyTimeout = null;
		this._supportedEvents = [
			'click', 'change', 'input', 'submit',
			'mouseenter', 'mouseleave', 'focusin', 'focusout',
			'touchstart', 'touchend', 'keyup', 'keydown'
		];
		this._originalCartRedirectAfterAdd = null;
		this._globalHandlers = {};

		// Ghost-click prevention registry.
		// Tracks elements that were already handled via touchend so the
		// subsequent synthetic click (if any) is silently ignored.
		this._touchedElements = new WeakMap();

		// Selectors that require a dedicated touchend handler because
		// TooltipHandler's preventDefault on touchstart suppresses the
		// browser-generated click event on touch devices.
		this._criticalTouchSelector = [
			'.wlfmc_add_to_wishlist',
			'.wlfmc_delete_item',
			'.wlfmc-wishlist-table .remove_from_wishlist',
			'.wlfmc_ajax_add_to_cart:not(.disabled)'
		].join(', ');
	}

	registerHandler(selector, eventName, handler, priority = 0, module = 'core') {
		if (!this._handlers.has(eventName)) {
			this._handlers.set(eventName, []);
		}
		const handlers = this._handlers.get(eventName);
		handlers.push({ handler, priority, module, selector, eventName });
		handlers.sort((a, b) => b.priority - a.priority);
		if (!this._registered) {
			this._registerGlobalListeners();
		}
	}

	registerModuleHandlers(moduleName, handlers) {
		handlers.forEach(({ selector, eventName = 'click', handler, priority = 0 }) => {
			this.registerHandler(selector, eventName, handler, priority, moduleName);
		});
		console.log(`[EventManager] ${handlers.length} handlers registered for ${moduleName}`);
	}

	registerCoreHandlers() {
		this.registerHandler('.wlfmc-list button[name="apply_bulk_actions"]', 'click', this._handleBulkAction.bind(this), 60);
		this.registerHandler('.wlfmc-gdpr-btn', 'click', this._handleGdpr.bind(this), 50);
		this.registerHandler('.wlfmc-close-notice', 'click', this._handleCloseNotice.bind(this), 40);
		this.registerHandler(
			'.wlfmc-wishlist-table .product-checkbox input[type="checkbox"], .wlfmc-save-for-later-table .product-checkbox input[type="checkbox"]',
			'change',
			this._handleCheckboxChange.bind(this),
			40
		);
		this.registerHandler('#bulk_add_to_cart, #bulk_add_to_cart2', 'change', this._handleBulkSelectChange.bind(this), 35);
		this.registerHandler(
			'.wlfmc-wishlist-table .quantity input, .wlfmc-wishlist-table .quantity select, .wlfmc-wishlist-table .quantity textarea, .wlfmc-save-for-later-table .quantity input, .wlfmc-save-for-later-table .quantity select, .wlfmc-save-for-later-table .quantity textarea',
			'change',
			this._handleQuantityChange.bind(this),
			30
		);
		this.registerHandler('.copy-link-trigger', 'click', this._handleCopyLink.bind(this), 20);

		// Counter remove buttons already have dedicated handlers.
		this.registerHandler('.wlfmc-products-counter-wishlist .remove_from_wishlist, .wlfmc-products-counter-waitlist .remove_from_wishlist', 'click', this._handleCounterRemove.bind(this), 90);
		this.registerHandler('.wlfmc-products-counter-wishlist .remove_from_wishlist, .wlfmc-products-counter-waitlist .remove_from_wishlist', 'touchend', this._handleCounterRemoveTouch.bind(this), 90);

		this.registerHandler('.wlfmc-list .product-components', 'click', this._handleProductComponents.bind(this), 60);
		this.registerHandler('.wlfmc-list .close-components', 'click', this._handleCloseComponents.bind(this), 60);

		// FIX: Dedicated touchend handler for critical wishlist actions.
		// TooltipHandler calls preventDefault() on touchstart for elements
		// inside .wlfmc-tooltip containers, which suppresses the synthetic
		// click event on touch devices. By listening to touchend directly
		// we guarantee the operation executes regardless of touchstart
		// interception.
		this.registerHandler(
			this._criticalTouchSelector,
			'touchend',
			this._handleCriticalTouch.bind(this),
			100
		);

		this._initPrepareQtyLinks();
		this._setupVariationHandlers();
		this._setupJQueryEventBridge();
		this._setupEventBusHandlers();

		document.addEventListener('adding_to_cart', (event) => {
			this._handleAddingToCart(event, event.detail?.button, event.detail?.data || {});
		});

		document.addEventListener('added_to_cart', (event) => {
			const { fragments, carthash, button } = event.detail || {};
			this._handleAddedToCart(event, fragments, carthash, button);
		});

		document.addEventListener('cart_page_refreshed', () => {
			this._handleCartPageRefreshed();
		});

		console.log('[EventManager] Core handlers registered');
	}

	/**
	 * Dedicated touchend handler for critical wishlist buttons.
	 *
	 * When TooltipHandler intercepts touchstart with preventDefault(),
	 * the browser never generates the subsequent click event.
	 * This handler executes the matching click handler immediately
	 * on touchend, then calls preventDefault() to suppress any
	 * residual synthetic click (belt-and-suspenders safety).
	 */
	_handleCriticalTouch(event, element) {
		// Ignore multi-touch gestures (pinch, etc.)
		if (event.touches && event.touches.length > 1) return;

		// Only respond to the primary touch point
		if (event.changedTouches && event.changedTouches.length > 1) return;

		// Record the touch so _globalHandler can ignore a potential
		// ghost click within the debounce window.
		this._touchedElements.set(element, Date.now());

		// Resolve matching click handlers in priority order.
		const clickHandlers = this._handlers.get('click') || [];
		let handled = false;

		for (const h of clickHandlers) {
			let validSelector = h.selector;
			if (validSelector.includes(':input')) {
				validSelector = validSelector.replace(/:input/g, 'input, select, textarea');
			}

			try {
				if (element.matches(validSelector)) {
					h.handler(event, element);
					handled = true;
					// Execute only the highest-priority match, consistent
					// with how _globalHandler resolves specificHandler.
					break;
				}
			} catch (err) {
				console.error(`[EventManager] Touch handler error for ${h.selector}:`, err);
			}
		}

		// Prevent the browser from dispatching a synthetic click event
		// after this touchend, avoiding double execution.
		if (handled && event.cancelable) {
			event.preventDefault();
		}
	}

	_handleCounterRemoveTouch(event, element) {
		event.preventDefault();
		this._handleCounterRemove(event, element);
	}

	_registerGlobalListeners() {
		const captureEvents = ['click', 'submit', 'focusin', 'focusout'];
		this._supportedEvents.forEach(eventName => {
			const useCapture = captureEvents.includes(eventName);
			const target = useCapture ? window : document;
			const handler = (e) => this._globalHandler(e, eventName);
			this._globalHandlers[eventName] = handler;

			// Touch events must be non-passive to allow preventDefault()
			// for ghost-click suppression on all browsers (especially Android).
			const listenerOptions = (eventName === 'touchstart' || eventName === 'touchend')
				? { capture: useCapture, passive: false }
				: useCapture;

			target.addEventListener(eventName, handler, listenerOptions);
		});
		this._registered = true;
	}

	_globalHandler(event, eventName) {
		const target = event.target;
		if (!target || !target.closest) return;

		// Ghost-click prevention: if a touchend handler already processed
		// this element, ignore the subsequent synthetic click event.
		if (eventName === 'click') {
			const element = target.closest(this._criticalTouchSelector);
			if (element) {
				const lastTouch = this._touchedElements.get(element);
				if (lastTouch && (Date.now() - lastTouch) < 1000) {
					// Clean up the entry so it doesn't leak indefinitely
					// (WeakMap handles GC, but explicit delete keeps the
					// debounce window tight).
					this._touchedElements.delete(element);
					return;
				}
			}
		}

		const handlers = this._handlers.get(eventName);
		if (!handlers || handlers.length === 0) return;

		const matchedHandlers = [];
		for (const h of handlers) {
			let validSelector = h.selector;
			if (validSelector.includes(':input')) {
				validSelector = validSelector.replace(/:input/g, 'input, select, textarea');
			}
			const element = target.closest(validSelector);
			if (element) {
				matchedHandlers.push({ ...h, element });
			}
		}

		if (matchedHandlers.length === 0) return;

		const specificHandler = matchedHandlers.find(h => h.selector.includes('['));
		const executed = new Set();

		for (const { handler, element, selector } of matchedHandlers) {
			if (specificHandler && selector !== specificHandler.selector && !selector.includes('[')) {
				continue;
			}
			if (!executed.has(selector)) {
				executed.add(selector);
				try {
					handler(event, element);
				} catch (error) {
					console.error(`[EventManager] Handler error for ${selector}:`, error);
				}
			}
		}

		if (event.cancelable && (eventName === 'click' || eventName === 'submit')) {
			// Allow native submissions from real submit controls (e.g. bulk
			// actions "Apply") outside of popup forms; popups prevent default
			// in their own handlers.
			const submitControl =
				eventName === 'click' &&
				target.closest('button[type="submit"], input[type="submit"], button:not([type])') &&
				!target.closest('.wlfmc-popup-form');
			if (!submitControl) {
				event.preventDefault();
			}
		}
	}

	_setupJQueryEventBridge() {
		const jQuery = window.jQuery;
		if (!jQuery) {
			console.warn('[EventManager] jQuery not available — WooCommerce event bridge skipped');
			return;
		}

		// Guard set to prevent infinite recursion.
		// Without this: jQuery.trigger → bridge → native dispatch → jQuery listener → bridge → ∞
		const _bridgingEvents = new Set();

		const guardBridge = (eventName, fn) => {
			if (_bridgingEvents.has(eventName)) return;
			_bridgingEvents.add(eventName);
			try {
				fn();
			} finally {
				setTimeout(() => _bridgingEvents.delete(eventName), 0);
			}
		};

		// Bridge: adding_to_cart (jQuery) → native CustomEvent
		jQuery(document.body).on('adding_to_cart', (ev, button, data) => {
			const nativeEvent = new CustomEvent('adding_to_cart', {
				bubbles: true,
				cancelable: true,
				detail: { button: ensureNative(button), data: data || {} }
			});
			document.dispatchEvent(nativeEvent);
			console.log('[EventManager] Bridge: adding_to_cart → native');
		});

		// Bridge: added_to_cart (jQuery) → native CustomEvent
		jQuery(document.body).on('added_to_cart', (ev, fragments, carthash, button) => {
			const nativeEvent = new CustomEvent('added_to_cart', {
				bubbles: true,
				cancelable: true,
				detail: { fragments, carthash, button: ensureNative(button) }
			});
			document.dispatchEvent(nativeEvent);
			console.log('[EventManager] Bridge: added_to_cart → native');
		});

		// Bridge: cart_page_refreshed (jQuery) → native CustomEvent
		jQuery(document.body).on('cart_page_refreshed', () => {
			const nativeEvent = new CustomEvent('cart_page_refreshed', {
				bubbles: true,
				cancelable: true,
				detail: {}
			});
			document.dispatchEvent(nativeEvent);
			console.log('[EventManager] Bridge: cart_page_refreshed → native');
		});

		// Bridge: add_to_cart_message (jQuery) → native CustomEvent
		jQuery(document.body).on('add_to_cart_message', (ev, message, button) => {
			const nativeEvent = new CustomEvent('add_to_cart_message', {
				bubbles: true,
				cancelable: true,
				detail: { message, button: ensureNative(button) }
			});
			document.dispatchEvent(nativeEvent);
			console.log('[EventManager] Bridge: add_to_cart_message → native');
		});

		// Bridge: wc_fragment_refresh (jQuery) is handled by third-party-integration (data-sync).

		// Plugin-specific events with recursion guard
		jQuery(document.body).on('wlfmc_adding_to_wishlist', () => {
			guardBridge('wlfmc_adding_to_wishlist', () => {
				document.body.dispatchEvent(new CustomEvent('wlfmc_adding_to_wishlist', { bubbles: true }));
			});
		});

		jQuery(document.body).on('wlfmc_adding_to_waitlist', () => {
			guardBridge('wlfmc_adding_to_waitlist', () => {
				document.body.dispatchEvent(new CustomEvent('wlfmc_adding_to_waitlist', { bubbles: true }));
			});
		});

		jQuery(document.body).on('wlfmc_adding_to_multi_list', () => {
			guardBridge('wlfmc_adding_to_multi_list', () => {
				document.body.dispatchEvent(new CustomEvent('wlfmc_adding_to_multi_list', { bubbles: true }));
			});
		});

		jQuery(document.body).on('wlfmc_added_to_wishlist', (ev, button, wrapper) => {
			guardBridge('wlfmc_added_to_wishlist', () => {
				const nativeEvent = new CustomEvent('wlfmc_added_to_wishlist', {
					bubbles: true,
					detail: { button, wrapper }
				});
				document.body.dispatchEvent(nativeEvent);
			});
		});

		// THIS WAS THE EXACT LINE CAUSING "too much recursion"
		jQuery(document.body).on('wlfmc_removed_from_wishlist', (ev, button, row, data) => {
			guardBridge('wlfmc_removed_from_wishlist', () => {
				const nativeEvent = new CustomEvent('wlfmc_removed_from_wishlist', {
					bubbles: true,
					detail: { button, row, data }
				});
				document.body.dispatchEvent(nativeEvent);
			});
		});

		jQuery(document.body).on('wlfmc_removed_from_waitlist', (ev, button, row, data) => {
			guardBridge('wlfmc_removed_from_waitlist', () => {
				const nativeEvent = new CustomEvent('wlfmc_removed_from_waitlist', {
					bubbles: true,
					detail: { button, row, data }
				});
				document.body.dispatchEvent(nativeEvent);
			});
		});

		jQuery(document.body).on('wlfmc_removed_from_save_for_later', (ev, button, row, data) => {
			guardBridge('wlfmc_removed_from_save_for_later', () => {
				const nativeEvent = new CustomEvent('wlfmc_removed_from_save_for_later', {
					bubbles: true,
					detail: { button, row, data }
				});
				document.body.dispatchEvent(nativeEvent);
			});
		});

		console.log('[EventManager] jQuery → Native event bridge registered (with recursion guard)');
	}

	// ---------- Qty Plus/Minus ----------
	_initPrepareQtyLinks() {
		// Add .show class to existing elements for CSS visibility
		const qtyContainers = document.querySelectorAll('.wlfmc-wishlist-table .quantity, .wlfmc-save-for-later-table .quantity');
		qtyContainers.forEach(qty => {
			if (qty.classList.contains('hidden')) return;
			const plus = qty.querySelector('.botiga-quantity-plus, a.plus, .ct-increase');
			const minus = qty.querySelector('.botiga-quantity-minus, a.minus, .ct-decrease');
			if (plus) plus.classList.add('show');
			if (minus) minus.classList.add('show');
		});

		// Use event delegation to handle clicks robustly.
		// This solves issues where theme JS adds the plus/minus buttons
		// AFTER our initialization or replaces the .quantity div entirely via AJAX fragments.
		if (this._qtyDelegated) return;
		this._qtyDelegated = true;

		document.addEventListener('click', (e) => {
			const plusSelector = '.wlfmc-wishlist-table .quantity .ct-increase, .wlfmc-wishlist-table .quantity .botiga-quantity-plus, .wlfmc-wishlist-table .quantity a.plus, .wlfmc-save-for-later-table .quantity .ct-increase, .wlfmc-save-for-later-table .quantity .botiga-quantity-plus, .wlfmc-save-for-later-table .quantity a.plus';
			const minusSelector = '.wlfmc-wishlist-table .quantity .ct-decrease, .wlfmc-wishlist-table .quantity .botiga-quantity-minus, .wlfmc-wishlist-table .quantity a.minus, .wlfmc-save-for-later-table .quantity .ct-decrease, .wlfmc-save-for-later-table .quantity .botiga-quantity-minus, .wlfmc-save-for-later-table .quantity a.minus';

			const plus = e.target.closest(plusSelector);
			const minus = e.target.closest(minusSelector);
			if (!plus && !minus) return;

			const qty = (plus || minus).closest('.quantity');
			if (!qty) return;

			// Intercept before theme handlers bubble up
			e.preventDefault();
			e.stopImmediatePropagation();

			const input = qty.querySelector('.qty');
			if (!input) return;

			let val = parseFloat(input.value) || 0;
			const step = parseFloat(input.step) || 1;

			if (plus) {
				const max = input.getAttribute('max') ? parseFloat(input.getAttribute('max')) : Infinity;
				input.value = val < max ? Math.round((val + step) * 100) / 100 : max;
			} else if (minus) {
				const min = input.getAttribute('min') ? Math.round(parseFloat(input.getAttribute('min')) * 100) / 100 : 0;
				input.value = val > min ? Math.round((val - step) * 100) / 100 : min;
			}

			// Trigger change event to fire the queue manager update
			input.dispatchEvent(new Event('change', { bubbles: true, cancelable: false }));
		}, true); // Capture phase ensures we catch it before WooCommerce/Theme handlers
	}

	// ---------- Event Bus Handlers ----------
	_setupEventBusHandlers() {
		// Content injected outside this module (tabbed navigation) needs qty links re-init.
		eventBus.on(EVENTS.CONTENT_UPDATED, () => {
			this._initPrepareQtyLinks();
		});

		eventBus.on('cart:message', (data) => {
			this._handleAddToCartMessage(data.message, data.button);
		});
	}

	_handleAddToCartMessage(message, button) {
		const wrapper = document.querySelector('.woocommerce-notices-wrapper .woocommerce-error, .woocommerce-notices-wrapper .woocommerce-message');
		if (button) button.classList.remove('loading');

		if (!wrapper) {
			const form = document.getElementById('wlfmc-wishlist-form');
			if (form) form.insertAdjacentHTML('afterbegin', message);
		} else {
			const noticesWrapper = wrapper.closest('.woocommerce-notices-wrapper');
			wrapper.style.opacity = '0';
			setTimeout(() => {
				if (noticesWrapper) {
					noticesWrapper.insertAdjacentHTML('beforebegin', message);
					noticesWrapper.remove();
				}
			}, 300);
		}
	}

	_handleBulkAction(event, element) {
		const wrapper = element.closest('.wlfmc-list') || element.closest('.action-wrapper');
		if (!wrapper) return;
		const select = wrapper.querySelector('select[name="bulk_actions"]');
		if (!select || !select.value) return;

		if (select.value === 'delete') {
			const form = wrapper.closest('form');
			if (form) form.querySelectorAll('input.qty').forEach(el => el.disabled = true);
		}
	}

	_handleCheckboxChange(event, element) {
		const p = element.parentElement;
		p.classList.remove('checked', 'unchecked');
		p.classList.add(element.checked ? 'checked' : 'unchecked');

		if (!element.checked) {
			const name = element.getAttribute('name');
			if (name) {
				document.querySelectorAll(`input[name="${name}"]`).forEach(cb => {
					cb.checked = false;
				});
			}
			const bulk1 = document.getElementById('bulk_add_to_cart');
			const bulk2 = document.getElementById('bulk_add_to_cart2');
			if (bulk1) bulk1.checked = false;
			if (bulk2) bulk2.checked = false;
		}

		// Scope to the current table (not document-wide) for correct isolation.
		const table = element.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table');
		if (!table) return;

		// Toggle move/copy buttons based on whether ANY row in THIS table is checked.
		const anyChecked = table.querySelectorAll('.product-checkbox input[type="checkbox"]:checked').length > 0;
		table.querySelectorAll('.multiple-product-move, .multiple-product-copy').forEach(link => {
			link.style.display = anyChecked ? '' : 'none';
		});

		// Rebuild the checked item-id list scoped to this table.
		let itemIds = '';
		table.querySelectorAll('.product-checkbox input[type="checkbox"]:checked').forEach(cb => {
			const cbRow = cb.closest('[data-item-id]');
			if (cbRow && cbRow.dataset.itemId) {
				itemIds += (itemIds ? ',' : '') + cbRow.dataset.itemId;
			}
		});

		table.querySelectorAll('.multiple-product-move, .multiple-product-copy').forEach(link => {
			link.dataset.itemId = itemIds;
			link.setAttribute('data-item-id', itemIds);
		});
	}

	_handleBulkSelectChange(event, element) {
		const checked = element.checked;
		const table = element.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table');
		if (!table) return;

		const checkboxes = table.querySelectorAll('[data-row-id] .product-checkbox input[type="checkbox"]:not(:disabled)');
		checkboxes.forEach(cb => {
			cb.checked = checked;
			cb.dispatchEvent(new Event('change', { bubbles: true }));
		});

		const bulk1 = document.getElementById('bulk_add_to_cart');
		const bulk2 = document.getElementById('bulk_add_to_cart2');
		if (bulk1) bulk1.checked = checked;
		if (bulk2) bulk2.checked = checked;
	}

	_handleQuantityChange(event, element) {
		clearTimeout(this._qtyTimeout);
		this._qtyTimeout = setTimeout(() => {
			const row = element.closest('[data-row-id]');
			if (!row) return;

			const productId = row.dataset.rowId;
			const cartItemKey = row.dataset.cartItemKey;
			const table = row.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table');
			const token = table?.dataset.token;
			const nonce = table?.dataset.nonce;
			const value = element.value;

			const addBtn = row.querySelector('.add_to_cart_button, .wlfmc_ajax_add_to_cart');
			if (addBtn) {
				addBtn.setAttribute('data-quantity', value);
				addBtn.setAttribute('href', (addBtn.getAttribute('href') || '').replace(/quantity=\d+/g, 'quantity=' + value));
			}

			// Action name is resolved mode-aware by the queue layer.
			const scope = table?.classList.contains('wlfmc-save-for-later-table')
				? 'save_for_later'
				: 'wishlist';

			queueManager.addWithDeduplication('update_item_quantity', {
				nonce,
				context: 'frontend',
				product_id: productId,
				cart_item_key: cartItemKey,
				wishlist_token: token,
				quantity: value,
				_table_scope: scope,
			}).then((result) => {
				// Point-update the row's data-quantity for consistency.
				if (result && result.success) {
					row.setAttribute('data-quantity', value);
					// Refresh server-rendered fragments (totals/prices/pagination),
					// same as legacy did after every quantity update.
					fragmentManager.loadFragments({ s: scope });
					eventBus.emit(EVENTS.CONTENT_UPDATED);
				}
			}).catch(() => { });
		}, 1000);
	}

	_handleCopyLink(event, element) {
		event.preventDefault();
		const url = element.dataset.href || element.getAttribute('data-href');
		if (!url) return;

		if (navigator.clipboard) {
			navigator.clipboard.writeText(url).then(() => {
				showSuccess(window.wlfmc_l10n?.labels?.link_copied);
			});
		} else {
			const hidden = document.createElement('input');
			hidden.value = url;
			hidden.type = 'text';
			document.body.appendChild(hidden);

			if (/ipad|iphone/i.test(navigator.userAgent)) {
				hidden.contentEditable = true;
				hidden.readOnly = false;
				const range = document.createRange();
				range.selectNodeContents(hidden);
				const sel = window.getSelection();
				sel.removeAllRanges();
				sel.addRange(range);
			} else {
				hidden.select();
			}

			document.execCommand('copy');
			document.execCommand('copy');
			hidden.remove();
			showSuccess(window.wlfmc_l10n?.labels?.link_copied);
		}
	}

	_handleAddingToCart(event, button, data) {
		if (button && button.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table')) {
			const table = button.closest('.wlfmc-wishlist-table, .wlfmc-save-for-later-table');
			if (table) {
				data.wishlist_id = table.dataset.id;
				data.wishlist_type = table.dataset.wishlistType;
				data.customer_id = table.dataset.customerId;
				data.is_owner = table.dataset.isOwner;

				if (typeof wc_add_to_cart_params !== 'undefined') {
					this._originalCartRedirectAfterAdd = wc_add_to_cart_params.cart_redirect_after_add;
					wc_add_to_cart_params.cart_redirect_after_add = window.wlfmc_l10n?.redirect_to_cart || '';
				}
			}
		}
	}

	_handleAddedToCart(event, fragments, carthash, button) {
		if (!button) return;

		if (typeof wc_add_to_cart_params !== 'undefined' && this._originalCartRedirectAfterAdd !== null) {
			wc_add_to_cart_params.cart_redirect_after_add = this._originalCartRedirectAfterAdd;
			this._originalCartRedirectAfterAdd = null;
		}

		button.classList.remove('added');

		const tr = button.closest('[data-row-id]');
		if (tr) {
			const addedToCart = tr.querySelector('.added_to_cart');
			if (addedToCart) addedToCart.remove();
		}

		const wishlistTable = button.closest('.wlfmc-wishlist-table');
		if (wishlistTable) {
			const removeAfterAdd = window.wlfmc_l10n?.remove_from_wishlist_after_add_to_cart;
			const fragmentEl = tr?.closest('.wlfmc-wishlist-fragment');
			const fragmentOptions = fragmentEl?.dataset?.fragmentOptions;
			let isUserOwner = true;

			if (fragmentOptions) {
				try {
					const opts = JSON.parse(fragmentOptions);
					isUserOwner = opts.is_user_owner !== false && opts.is_user_owner !== 'false';
				} catch (e) {
					isUserOwner = true;
				}
			}

			if (removeAfterAdd && isUserOwner) {
				const row = button.closest('[data-row-id]');
				if (row) {
					const rowId = row.dataset.rowId;
					const table = row.closest('.wlfmc-wishlist-fragment');
					const listType = table?.querySelector('.wlfmc-wishlist-table')?.dataset.wishlistType || 'wishlist';
					const wishlistId = table?.querySelector('.wlfmc-wishlist-table')?.dataset.id;

					if (rowId) {
						eventBus.emit('cart:item_added_to_cart', { rowId, wishlistId, listType, isUserOwner, button });
					}
				}
			}
		}

		const sflTable = button.closest('.wlfmc-save-for-later-table');
		if (sflTable) {
			const row = button.closest('[data-item-id]');
			if (row) {
				const itemId = row.dataset.itemId;
				const fragmentEl = row.closest('.wlfmc-wishlist-fragment');
				const fragmentOptions = fragmentEl?.dataset?.fragmentOptions;
				let isUserOwner = true;

				if (fragmentOptions) {
					try {
						const opts = JSON.parse(fragmentOptions);
						isUserOwner = opts.is_user_owner !== false && opts.is_user_owner !== 'false';
					} catch (e) {
						isUserOwner = true;
					}
				}

				if (itemId && isUserOwner) {
					eventBus.emit('cart:sfl_item_added_to_cart', { itemId });
				}
			}
		}
	}

	_handleCartPageRefreshed() {
		this._initPrepareQtyLinks();
		eventBus.emit(EVENTS.CONTENT_UPDATED);
	}

	_handleCounterRemove(event, element) {
		// Detect the counter type from the wrapper attribute (the item container
		// class is shared between wishlist/waitlist and cannot be trusted here).
		const wrapperEl = element.closest('.wlfmc-counter-wrapper');
		const isWishlist = String(wrapperEl?.dataset.counterType || 'wishlist') !== 'waitlist';
		const productId = extractProductId(element);
		if (!productId) { return; }

		const row = element.closest('[data-row-id]');
		let wishlistId = getWishlistIdFromElement(element);
		if (!wishlistId && row) {
			wishlistId =
				parseInt(row.dataset.wishlistId || row.getAttribute('data-wishlist-id') || '0', 10) || 0;
		}

		const nonce = element.dataset.nonce || element.getAttribute('data-nonce');
		const wishlistToken = row?.getAttribute('data-wishlist-token') || row?.dataset?.wishlistToken || '';

		eventBus.emit('counter:remove', {
			isWishlist,
			productId,
			wishlistId,
			nonce,
			wishlistToken,
			element
		});
	}

	_handleProductComponents(event, element) {
		event.preventDefault();
		const row = element.closest('tr');
		if (!row) return;

		const metaData = row.querySelector('.wlfmc-absolute-meta-data');
		const nextRow = row.nextElementSibling;
		const isNextHidden = nextRow && nextRow.classList.contains('hide');

		if (metaData) {
			metaData.style.display = (metaData.style.display === 'none' || metaData.style.display === '') ? 'block' : 'none';
		}

		if (nextRow && nextRow.classList.contains('wlfmc-row-meta-data')) {
			nextRow.classList.toggle('hide');
		}

		row.classList.toggle('show-meta-data', isNextHidden);
	}

	_handleCloseComponents(event, element) {
		event.preventDefault();
		const row = element.closest('tr');
		if (!row) return;

		const metaData = row.querySelector('.wlfmc-absolute-meta-data');
		if (metaData) metaData.style.display = 'none';
		row.classList.remove('show-meta-data');

		const nextRow = row.nextElementSibling;
		if (nextRow && nextRow.classList.contains('wlfmc-row-meta-data')) {
			nextRow.classList.add('hide');
		}
	}

	_setupVariationHandlers() {
		const jQuery = window.jQuery;
		if (!jQuery) {
			console.warn('[EventManager] jQuery not available for variation bridge');
			return;
		}

		const _variationGuard = {
			reset_data: false,
			hide_variation: false,
		};

		jQuery(document).on('found_variation', (ev, variation) => {
			const target = jQuery(ev.target);
			const productId = target.data('product_id');
			const variationData = { ...variation, product_id: productId };
			eventBus.emit('variation:shown', variationData);
		});

		jQuery(document).on('reset_data', (ev) => {
			if (_variationGuard.reset_data) return;
			_variationGuard.reset_data = true;

			const target = jQuery(ev.target);
			const productId = target.data('product_id');
			if (productId) {
				eventBus.emit('variation:reset', { productId: parseInt(productId, 10) });
			}

			setTimeout(() => { _variationGuard.reset_data = false; }, 0);
		});

		jQuery(document).on('hide_variation', '.variations_form', (ev) => {
			if (_variationGuard.hide_variation) return;
			_variationGuard.hide_variation = true;

			const target = jQuery(ev.target);
			const productId = target.data('product_id');
			if (productId) {
				eventBus.emit('variation:hide', { productId: parseInt(productId, 10) });
			}

			setTimeout(() => { _variationGuard.hide_variation = false; }, 0);
		});

		jQuery(document).on('show_variation', '.variations_form', (ev, variation) => {
			//
		});

		console.log('[EventManager] Variation bridge handlers registered (with recursion guard)');
	}

	_handleGdpr(event, element) {
		const actionType = element.dataset.action;
		const customerId = parseInt(element.dataset.cid, 10);
		const nonce = element.dataset.nonce;
		if (!actionType || !customerId) return;

		if (element.querySelector('i') !== null) {
			element.classList.add('wlfmc-action', 'wlfmc-loading');
		} else {
			element.classList.add('wlfmc-action', 'wlfmc-loading-alt');
		}

		const gdprAction = window.wlfmc_l10n?.actions?.gdpr_action || 'wlfmc_change_gdpr_status';

		queueManager.addWithDeduplication('gdpr_action', {
			action: gdprAction,
			action_type: actionType,
			cid: customerId,
			nonce,
			context: 'frontend'
		})
			.then((result) => {
				if (result.success || result.result === 'true') {
					document.querySelectorAll('.wlfmc-gdpr-notice-wrapper, .wlfmc-unsubscribe-notice-wrapper').forEach(el => el.remove());
				}
			})
			.catch(() => { })
			.finally(() => {
				element.classList.remove('wlfmc-loading', 'wlfmc-loading-alt');
			});
	}

	_handleCloseNotice(event, element) {
		if (!validators.isCookieEnabled()) {
			window.alert(window.wlfmc_l10n?.labels?.cookie_disabled);
			return;
		}

		const wrapper = element.closest('.wlfmc-notice-wrapper');
		if (wrapper) {
			wrapper.style.opacity = '0';
			setTimeout(() => wrapper.remove(), 300);
		}

		validators.setCookie('wlfmc_save_for_later_notice', 'true', 365 * 25);
	}

	destroy() {
		const captureEvents = ['click', 'submit', 'focusin', 'focusout'];
		this._supportedEvents.forEach(eventName => {
			const handler = this._globalHandlers[eventName];
			if (handler) {
				const useCapture = captureEvents.includes(eventName);
				const target = useCapture ? window : document;
				const listenerOptions = (eventName === 'touchstart' || eventName === 'touchend')
					? { capture: useCapture, passive: false }
					: useCapture;
				target.removeEventListener(eventName, handler, listenerOptions);
			}
		});

		this._handlers.clear();
		this._globalHandlers = {};
		this._registered = false;
		this._touchedElements = new WeakMap();

		const jQuery = window.jQuery;
		if (jQuery) {
			jQuery(document.body).off('adding_to_cart added_to_cart cart_page_refreshed add_to_cart_message');
			jQuery(document.body).off('wlfmc_adding_to_wishlist wlfmc_adding_to_waitlist wlfmc_adding_to_multi_list');
			jQuery(document.body).off('wlfmc_added_to_wishlist wlfmc_removed_from_wishlist');
			jQuery(document.body).off('wlfmc_removed_from_waitlist wlfmc_removed_from_save_for_later');
			jQuery(document).off('found_variation reset_data');
			jQuery(document).off('hide_variation', '.variations_form');
			jQuery(document).off('show_variation', '.variations_form');
		}
	}
}

export const eventManager = new EventManager();
export default eventManager;
